import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from './ThemeToggle';
import { LocaleToggle } from './LocaleToggle';
import { UserMenu } from './UserMenu';
import { OrgSwitcher } from './OrgSwitcher';

export interface TopbarProps {
 /** Triggered by the hamburger button on small screens. */
 onOpenMobileNav?: () => void;
 /** Triggered when the user clicks the command palette button. */
 onOpenCommandPalette?: () => void;
 /** Live unread notification count. */
 notificationCount?: number;
 className?: string;
}

/**
 * Top application bar — visible above all authenticated pages. Holds the
 * mobile-nav trigger, command palette shortcut, org switcher, notifications,
 * theme/locale toggles, and the user menu.
 */
export function Topbar({
 onOpenMobileNav,
 onOpenCommandPalette,
 notificationCount = 0,
 className,
}: TopbarProps) {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const isMac =
 typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

 return (
 <header
 className={cn(
 'sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background px-3 sm:px-4',
 className,
 )}
 >
 <Button
 variant="ghost"
 size="icon"
 className="lg:hidden"
 onClick={onOpenMobileNav}
 aria-label={t('common.open_menu')}
 >
 <Menu className="h-5 w-5" />
 </Button>

 <div className="hidden lg:block">
 <OrgSwitcher />
 </div>

 <div className="flex flex-1 items-center justify-center sm:justify-start sm:pl-4">
 <button
 type="button"
 onClick={onOpenCommandPalette}
 className={cn(
 'group inline-flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground',
 'transition-colors hover:border-ring hover:text-foreground',
 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
 )}
 aria-label={t('common.command_palette')}
 >
 <Search className="h-4 w-4" aria-hidden="true" />
 <span className="hidden flex-1 truncate text-left sm:inline">
 {t('common.command_palette_hint')}
 </span>
 <kbd className="ml-auto hidden rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase text-muted-foreground sm:inline">
 {isMac ? '⌘K' : 'Ctrl K'}
 </kbd>
 </button>
 </div>

 <div className="ml-auto flex items-center gap-1">
 <Button
 variant="ghost"
 size="icon"
 aria-label={
 notificationCount > 0
 ? `${t('common.notifications')} (${notificationCount})`
 : t('common.notifications')
 }
 className="relative"
 onClick={() => navigate('/notifications')}
 >
 <Bell className="h-4 w-4" />
 {notificationCount > 0 && (
 <Badge
 variant="destructive"
 className="absolute -right-0.5 -top-0.5 h-4 min-w-[16px] justify-center rounded-full px-1 py-0 text-[10px]"
 >
 {notificationCount > 99 ? '99+' : notificationCount}
 </Badge>
 )}
 </Button>
 <LocaleToggle />
 <ThemeToggle />
 <UserMenu />
 </div>
 </header>
 );
}
